import React, { useState } from "react";
import { FormField } from "cxa-form-field";
import { ACCEPT_FILES, MAX_FILE_SIZE } from "../constants";
import { getFilePropsPP } from "cxa-utils";
import { useSelector, useDispatch } from "react-redux";
import { renderRejectedFiles, resizeImage } from "./util";
import Dropzone from "../common/file-uploader/drop-zone";
import UploaderIcon from "./widgets/UploaderIcon";
import withMatchMediaProps from "@hocs/with-match-media-props";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";
import { DeleteIcon, ChevronLeftIcon, ChevronRightIcon } from "../common/icons";
import PDFViewer from "./PDFViewer";
import {
  removeDocument,
  setRejectedSupportingDocumentFiles,
  setSupportingDocuments,
  removeSupportingDocument,
} from "../../actions/claimActionsPP";

export const Document = ({ document, onDelete, allowDelete = true, isNarrowScreen }) => {
  return (
    <div>
      {allowDelete && (
        <DeleteIcon
          onClick={onDelete}
          className="vector-icons-icon-color"
          style={{
            float: "right",
            position: "relative",
            top: ".2rem",
            width: "1.5rem",
            height: "1.5rem",
            right: ".03rem",
            cursor: "pointer",
          }}
        />
      )}
      {document.contentType == "application/pdf" ? (
        <PDFViewer pdfUrl={document.fileUrl} size="small" />
      ) : (
        <figure key={document.fileUrl} id="pdf-main-container">
          {document.isLocalFile ? (
            <img
              src={document.fileUrl}
              alt=""
              style={{
                width: isNarrowScreen ? "115px" : "220px",
                height: isNarrowScreen ? "160px" : "254px",
              }}
            />
          ) : (
            <a href={document.fileUrl} target="_blank" rel="noopener noreferrer">
              {document.contentType && document.contentType.includes("image") && (
                <img
                  src={document.fileUrl}
                  alt=""
                  style={{
                    width: isNarrowScreen ? "115px" : "220px",
                    height: isNarrowScreen ? "160px" : "254px",
                  }}
                />
              )}
            </a>
          )}
        </figure>
      )}
    </div>
  );
};

const DocumentUploader = ({
  name,
  value,
  onChange,
  onBlur,
  onDrop,
  meta,
  error,
  allowDelete,
  isNarrowScreen,
  params,
  ...rest
}) => {
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const dispatch = useDispatch();
  const imageList = value || [];
  const claimsData = useSelector(state => state.claimsPP);

  const visibleImages =
    Array.isArray(imageList) && imageList.length > 2
      ? imageList.slice(currentIndex, currentIndex + 3)
      : imageList.concat([]);

  const renderDocuments = () => {
    const updatedImageList = imageList.slice();
    const onDelete = (file, isLocalFile = false) => {
      const filteredImageList = updatedImageList.filter(item => item !== file);
      dispatch(removeSupportingDocument(file.preview));
      onChange(filteredImageList);
      !isLocalFile && dispatch(removeDocument(file.objectId));
    };

    return (
      <div
        className="carousel-inner"
        style={{ display: "flex", overflowX: "auto", whiteSpace: "nowrap", gap: "1px" }}
      >
        {updatedImageList.slice(currentIndex, currentIndex + 3).map(file => {
          const doc = getFilePropsPP(file);
          return (
            <div key={doc.fileUrl} style={{ display: "inline-block", margin: "0 3px" }}>
              <Document
                document={doc}
                onDelete={() => onDelete(file, doc.isLocalFile)}
                isNarrowScreen={isNarrowScreen}
              />
            </div>
          );
        })}
      </div>
    );
  };

  let fileList = imageList.length > 0 ? [...imageList] : [];

  return (
    <div>
      <div className="image-carousel">
        {currentIndex >= 1 && (
          <button
            style={{
              backgroundColor: "transparent",
              border: "none",
              padding: "0",
              cursor: "pointer",
            }}
            onClick={() => setCurrentIndex(currentIndex - 1)}
            type="button"
          >
            <ChevronLeftIcon />
          </button>
        )}
        <div className="carousel-container">
          <div className="image-carousel">
            {!claimsData.isCameraStarted && renderDocuments()}
            {imageList.length <= 4 && visibleImages.length <= 2 && (
              <div className="uploader-container">
                {!claimsData.isCameraStarted && (
                  <Dropzone
                    accept={ACCEPT_FILES}
                    maxSize={MAX_FILE_SIZE}
                    keepSelection={true}
                    ruleOfThirds={true}
                    multiple={false}
                    onDrop={(files, rejectedFiles) => {
                      files.forEach(file => {
                        dispatch(setSupportingDocuments(file));
                        resizeImage(file, fileList, onChange);
                      });

                      if (Array.isArray(rejectedFiles) && rejectedFiles.length > 0) {
                        setRejectedFiles(rejectedFiles);
                        dispatch(setRejectedSupportingDocumentFiles(rejectedFiles));
                      } else {
                        setRejectedFiles([]);
                      }
                    }}
                    {...rest}
                  >
                    <UploaderIcon className="uploader" size="small" isMandatory={false} />
                  </Dropzone>
                )}
              </div>
            )}
          </div>
        </div>
        {currentIndex <= 1 && visibleImages.length >= 2 && imageList.length <= 5 && (
          <button
            style={{
              backgroundColor: "transparent",
              border: "none",
              padding: "0",
              cursor: "pointer",
            }}
            onClick={() => setCurrentIndex(currentIndex + 1)}
            type="button"
          >
            <ChevronRightIcon fill="black" />
          </button>
        )}
      </div>
      {rejectedFiles.length > 0 && renderRejectedFiles(rejectedFiles)}
    </div>
  );
};

const DocumentField = props => (
  <FormField multiple={true} component={DocumentUploader} {...props} />
);

DocumentUploader.propTypes = {
  isNarrowScreen: PropTypes.shape({
    maxWidth: PropTypes.number.isRequired,
  }).isRequired,
  params: PropTypes.object,
};

Document.propTypes = {
  allowDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  document: PropTypes.object,
  isNarrowScreen: PropTypes.bool.isRequired,
};

export default injectIntl(
  withMatchMediaProps({
    isNarrowScreen: {
      maxWidth: 680,
    },
  })(DocumentField)
);



// WEBPACK FOOTER //
// ./components/claim/DocumentField.js